import * as Sentry from '@sentry/vue';
import { useIntervalFn } from '@vueuse/core';
import { MetaObject } from '@xeokit/xeokit-sdk';

import {
  Model3dCreateDto,
  Model3dEntryResponse,
  Model3dEntryStatus,
  ProcessFileType,
  ProcessType,
  ProjectEntryResponse,
  byteToMb,
  getProcessFileType,
} from '@xeovision/shared';
import { CanceledError } from 'axios';
import { toast } from 'vue-sonner';
import {
  loadModelBIM,
  loadModelGLTF,
  loadModelLAS,
  loadModelOBJ,
  loadModelSTL,
  loadModelXKT,
} from '~/utils/viewer/loaders';
import { getAABBCenter } from '~/utils/viewer/models-distance';

import xxhash from 'xxhash-wasm';
import { WorkerMessage, WorkerMessageData } from '~/workers/ifc-xkt-worker';

export enum ModelListElementStatus {
  PENDING = 'pending',
  DOWNLOADING = 'downloading',
  UPLOADING = 'uploading',
  UPLOADED = 'uploaded',
  LOCAL_CONVERTION = 'local_conversion',
  LOCAL_CONVERTION_ERROR = 'local_conversion_error',
  LAUNCHING = 'launching',
  LAUNCHED = 'launched',
  RETRIABLE_ERROR = 'retriable_error',
  CRITICAL_ERROR = 'critical_error',
}

export enum ModelListElementViewerStatus {
  PENDING = 'pending',
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
}

export interface ModelListElement {
  name: string;

  size: number;
  status: ModelListElementStatus;
  errorMessage: string | null;
  removed: boolean;

  model3dEntry: Model3dEntryResponse | null;

  localConversion: {
    worker: Worker | null;
    active: boolean;
    done: boolean;
    statusMessage: string;
    progress: number;
    progressText: string;
  };

  viewer: {
    status: ModelListElementViewerStatus;
    errorMessage: string | null;
    visibility: boolean;

    positionOffsetX: number;
    positionOffsetY: number;
    positionOffsetZ: number;

    rotationOffsetX: number;
    rotationOffsetY: number;
    rotationOffsetZ: number;
  };

  files: {
    id: string;
    input: boolean;
    hash?: string;
    uploadUrl: string | null;
    data: File | null;
    name: string;
    mimeType: string;
    ext: string;
    size: number;
    progress: number;
    speed: number;
    timeLeft: number;
    abortController: AbortController;
    isUploaded: boolean;
    isReported: boolean;
  }[];

  sceneModelIds: string[];
}

export interface FileURL {
  name: string;
  size: number;
  lastModified?: string;
  type: string;
  url: string;
}

export const useProjectStore = defineStore('project-store', () => {
  const { $i18n } = useNuxtApp();
  const viewerStore = useViewerStore();
  const uiStore = useUiStore();
  const featureFlagsStore = useFeatureFlagsStore();
  const queueStore = useQueueStore();
  const runtimeConfig = useRuntimeConfig();
  const processesStore = useProcessesStore();
  const route = useRoute();

  const { t } = $i18n;
  const { models: projectModels } = storeToRefs(processesStore);

  const project = ref<ProjectEntryResponse | null>(null);
  const modelsList = ref<ModelListElement[]>([]);

  const modelsStats = ref({
    inProgress: 0,
    conversionProgress: 0,
    uploadProgress: 0,

    uploading: 0,
    uploaded: 0,
    converted: 0,
    converting: 0,
  });

  const hasPointCloudModel = ref(false);

  let refreshAbortController;
  const refreshIntervalEnabled = ref(true);

  let metaObjectsArray: [string, MetaObject][];
  const metaObjectsArrayReady: Ref<boolean> = ref(false);

  function getMetaObjectsArray() {
    return metaObjectsArray;
  }

  useIntervalFn(() => {
    if (refreshIntervalEnabled.value) refreshProject();
  }, 2000);

  // https://technical.buildingsmart.org/standards/ifc/ifc-formats/
  const allValidExtensions = [
    'ifc',
    'glb',
    'xkt',
    'las',
    'laz',
    'obj',
    'stl',
    'bim',
    'step',
    'stp',
    // 'zip',
    'nwc',
    'nwd',
    'rfa',
    'rvt',
  ];

  // Extensions of internal features are hidden from users without the role
  const validExtensions = computed(() =>
    allValidExtensions.filter((ext) => {
      const fileType = getProcessFileType(ext);
      return !fileType || !featureFlagsStore.hiddenProcessFileTypes.includes(fileType);
    }),
  );

  async function createProject(captchaToken?: string) {
    const { projects } = useHttpClientStore();

    const response = await projects.createOne({
      captchaToken,
    });

    project.value = response.data;
  }

  async function cloneProject(projectId: string, captchaToken?: string) {
    const { projects } = useHttpClientStore();

    const response = await projects.cloneOne({
      projectId,
      captchaToken,
    });

    project.value = response.data;
  }

  async function refreshProject(id?: string) {
    if (refreshAbortController) return;

    const projectId = id ?? project.value?.id;
    if (!projectId) return;

    const { projects } = useHttpClientStore();

    refreshAbortController = new AbortController();
    const signal = refreshAbortController.signal;
    const response = await projects.getOne(projectId, signal);
    refreshAbortController = null;

    project.value = response.data;

    if (route.name === 'project-id') {
      let title = `${t('pages.project.title')} - ${t('general.app_name')}`;
      if (project.value.name) title = `${project.value.name} - ${title}`;
      useHead({ title });
    }

    if (!project.value.model3dEntries) return;

    for (const model3dEntry of project.value.model3dEntries) {
      const element = modelsList.value.find(
        (element) => element.model3dEntry?.id === model3dEntry.id,
      );

      if (element?.removed) continue;

      let status = element?.status ?? ModelListElementStatus.LAUNCHED;
      let errorMessage: string | null = element?.errorMessage ?? null;

      if (model3dEntry.status === Model3dEntryStatus.PROCESS_ERROR) {
        status = ModelListElementStatus.CRITICAL_ERROR;
        errorMessage = 'composables.use_model_list.error_process';

        processesStore.showProcessToast(model3dEntry, 'error', {
          title: 'Model processing error',
          description: `An error occurred while converting the model ${model3dEntry.inputFileName}`,
          action: {
            label: 'More',
            onClick: () => console.log(`More error clicked!`, { model3dEntry }),
          },
        });
      }

      if (model3dEntry.status === Model3dEntryStatus.PROCESS_COMPLETED) {
        if (uiStore.uploaderAutoCloseAllowed) {
          uiStore.uploaderDialogVisible = false;
        }

        processesStore.showProcessToast(model3dEntry, 'success', {
          title: 'Model processing complete',
          description: `The model ${model3dEntry.inputFileName} load completed`,
        });
      }

      if (!element) {
        const name = model3dEntry.inputFileName;
        const size = model3dEntry.inputFileSize ?? 0;

        if (!name) return;

        if (
          model3dEntry.status === Model3dEntryStatus.CREATED ||
          model3dEntry.status === Model3dEntryStatus.READY_FOR_UPLOAD
        ) {
          status = ModelListElementStatus.CRITICAL_ERROR;
          errorMessage = 'composables.use_model_list.error_input';
        }

        if (model3dEntry.status === Model3dEntryStatus.INPUT_UPLOADED) {
          status = ModelListElementStatus.RETRIABLE_ERROR;
          errorMessage = 'composables.use_model_list.error_launch';
        }

        modelsList.value.push({
          name,
          size,
          status,
          removed: false,
          localConversion: {
            worker: null,
            active: false,
            done: false,
            statusMessage: '',
            progress: 0,
            progressText: '',
          },
          errorMessage,
          model3dEntry,
          files: [],
          viewer: {
            status: ModelListElementViewerStatus.PENDING,
            errorMessage: null,
            visibility: true,

            positionOffsetX: 0,
            positionOffsetY: 0,
            positionOffsetZ: 0,

            rotationOffsetX: 0,
            rotationOffsetY: 0,
            rotationOffsetZ: 0,
          },
          sceneModelIds: [],
        });
      } else {
        element.model3dEntry = model3dEntry;
        element.status = status;
        element.errorMessage = errorMessage;
      }
    }

    if (viewerStore.isInitialized) {
      for (const element of modelsList.value) {
        if (element.viewer.status !== ModelListElementViewerStatus.PENDING) continue;
        if (!element.model3dEntry?.canLoad) continue;
        loadToViewer(element);
      }

      metaObjectsArrayReady.value = true;

      queueStore.addJob({
        id: `${id}-annotations`,
        handler: async () => {
          viewerStore.loadAnnotations();
        },
      });

      if (route.query['no-check-distance'] !== 'true') {
        queueStore.addJob({
          id: `${id}-check-distance`,
          handler: async () => {
            viewerStore.checkModelsDistance();
          },
        });
      }
    }

    updateStats();
  }

  async function createModel(file: File | FileURL): Promise<ModelListElement | null> {
    if (!project.value?.id) return null;

    const fileId = `${file.name}-${file.size}-${file.lastModified}-${file.type}`;
    const fileExt = file.name.split('.').pop() as string;

    const findSameFile = modelsList.value.find((element) => {
      const inputFile = getInputFile(element);
      return inputFile && inputFile.id === fileId;
    });

    if (findSameFile) return null;

    if (!validExtensions.value.includes(fileExt.toLowerCase())) {
      toast.error(t('composables.use_model_list.error_extension_title'), {
        description: t('composables.use_model_list.error_extension_description', {
          extensions: '.' + validExtensions.value.join(', .'),
        }),
        duration: 3000,
      });

      return null;
    }

    if (modelsList.value.length >= runtimeConfig.public.MAX_MODELS_PER_PROJECT) {
      toast.error(t('composables.use_model_list.error_max_models_title'), {
        description: t('composables.use_model_list.error_max_models_description', {
          fileName: file.name,
          maxModels: runtimeConfig.public.MAX_MODELS_PER_PROJECT,
        }),
        duration: 3000,
      });

      return null;
    }

    const fileSizeInMb = byteToMb(file.size);
    const maxFileSizeInGb =
      Math.round((runtimeConfig.public.MAX_FILE_SIZE_IN_MB / 1024) * 1000) / 1000;

    if (fileSizeInMb > runtimeConfig.public.MAX_FILE_SIZE_IN_MB) {
      toast.error(t('composables.use_model_list.error_file_size_title'), {
        description: t('composables.use_model_list.error_file_size_description', {
          size: maxFileSizeInGb,
        }),
        duration: 3000,
      });

      return null;
    }

    modelsList.value.push({
      name: file.name,
      size: file.size,
      localConversion: {
        worker: null,
        active: false,
        done: false,
        statusMessage: '',
        progress: 0,
        progressText: '',
      },
      removed: false,
      status: ModelListElementStatus.PENDING,
      errorMessage: null,

      model3dEntry: null,

      files: [
        {
          id: fileId,
          input: true,
          uploadUrl: null,
          data: null,
          name: file.name,
          mimeType: file.type,
          ext: fileExt,
          size: file.size,
          progress: 0,
          speed: 0,
          timeLeft: 0,
          abortController: new AbortController(),
          isUploaded: false,
          isReported: false,
        },
      ],

      viewer: {
        status: ModelListElementViewerStatus.PENDING,
        errorMessage: null,
        visibility: true,

        positionOffsetX: 0,
        positionOffsetY: 0,
        positionOffsetZ: 0,

        rotationOffsetX: 0,
        rotationOffsetY: 0,
        rotationOffsetZ: 0,
      },

      sceneModelIds: [],
    });

    const element = modelsList.value[modelsList.value.length - 1];

    if (file instanceof File) {
      element.files[0].data = file;
      return element;
    } else {
      try {
        element.status = ModelListElementStatus.DOWNLOADING;
        element.files[0].progress = 0;

        const response = await downloadFile(
          file.url,
          file.name,
          element.files[0].abortController,
          (data) => {
            element.files[0].progress = data.progress;
            element.files[0].speed = data.speed;
            element.files[0].timeLeft = data.timeLeft;
          },
        );

        element.files[0].data = response;
        element.status = ModelListElementStatus.PENDING;
        element.files[0].progress = 0;
        element.files[0].speed = 0;
        element.files[0].timeLeft = 0;

        return element;
      } catch (error) {
        if (error instanceof CanceledError) {
          modelsList.value = modelsList.value.filter((item) => item !== element);
        } else {
          element.status = ModelListElementStatus.CRITICAL_ERROR;
          element.errorMessage = 'composables.use_model_list.error_download_element';
          Sentry.captureException(error, {
            fingerprint: ['useProjectStore', 'createModel', 'fileName', file.name],
          });
        }

        return null;
      }
    }
  }

  async function calculateModelHash(element: ModelListElement) {
    const inputFile = getInputFile(element);

    if (element.localConversion.active) return;
    if (!inputFile) return;

    if (inputFile.data instanceof File) {
      const fileReader = inputFile.data.stream().getReader();
      const { create64 } = await xxhash();
      const hashFactor = create64();

      while (true) {
        const { done, value: chunk } = await fileReader.read();
        if (done) break;

        if (chunk) {
          hashFactor.update(chunk);
        }
      }

      if (route.query['no-cache'] !== 'true' && !featureFlagsStore.modes.modeDebug)
        inputFile.hash = hashFactor.digest().toString(16).padStart(16, '0');
    }
  }

  async function initializeModel(element: ModelListElement) {
    const inputFile = getInputFile(element);

    if (!project.value?.id || element.model3dEntry || !inputFile) return;
    if (element.status !== ModelListElementStatus.PENDING) return;

    try {
      const { models3d } = useHttpClientStore();

      const outputs: Model3dCreateDto['outputs'] = [];

      for (const file of element.files) {
        if (file.input) continue;

        outputs.push({
          fileName: file.name,
        });
      }

      const response = await models3d.createOne({
        projectEntryId: project.value.id,
        fileName: inputFile.name,
        fileHash: inputFile.hash,
        fileSize: inputFile.size,
        outputs,
      });

      if (response.data.uploadList.length === 0) {
        const modelEntry = await models3d.getOne(response.data.model3dEntry.id);
        element.model3dEntry = modelEntry.data;

        element.status = ModelListElementStatus.LAUNCHED;
        element.files = [];

        return;
      }

      for (const uploadElement of response.data.uploadList) {
        const file = element.files.find((file) => file.name === uploadElement.fileName);
        if (file) file.uploadUrl = uploadElement.urls[0].uploadUrl;
      }

      element.model3dEntry = response.data.model3dEntry;
    } catch (error) {
      element.status = ModelListElementStatus.RETRIABLE_ERROR;
      element.errorMessage = 'composables.use_model_list.error_initialize_element';
      // No model3dEntry exists yet when creating it fails, so fingerprint by file name.
      Sentry.captureException(error, {
        fingerprint: ['useProjectStore', 'initializeModel', 'fileName', inputFile.name],
      });
    }
  }

  async function uploadModel(element: ModelListElement) {
    const inputFile = getInputFile(element);

    if (!inputFile) return;
    if (inputFile.isReported) return;
    if (element.removed) return;
    if (!element.model3dEntry) return;

    element.status = ModelListElementStatus.UPLOADING;

    try {
      const { models3d } = useHttpClientStore();
      const uploadPromises: (() => Promise<void>)[] = [];

      for (const file of element.files) {
        file.abortController = new AbortController();
        file.progress = 0;
        file.speed = 0;
        file.timeLeft = 0;

        if (!file.isUploaded && file.uploadUrl) {
          uploadPromises.push(() =>
            uploadFile(file.uploadUrl!, file.data!, file.abortController, (data) => {
              file.progress = data.progress;
              file.speed = data.speed;
              file.timeLeft = data.timeLeft;
            }).then(() => {
              file.isUploaded = true;
            }),
          );
        }
      }

      await Promise.all(uploadPromises.map((promise) => promise()));

      await models3d.reportUpload(element.model3dEntry.id);
      for (const file of element.files) file.isReported = true;

      element.status = ModelListElementStatus.UPLOADED;
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await refreshProject();
    } catch (error) {
      if (error instanceof CanceledError) {
        element.status = ModelListElementStatus.RETRIABLE_ERROR;
        element.errorMessage = 'composables.use_model_list.error_canceled';
        return;
      }

      element.status = ModelListElementStatus.RETRIABLE_ERROR;
      element.errorMessage = 'composables.use_model_list.error_upload_element';

      const sentryId = Sentry.captureException(error, {
        fingerprint: [' ', 'uploadModel', 'model3dEntryId', element.model3dEntry!.id],
      });

      uiStore.troubleshootDialogSentryErrorId = sentryId;
    }
  }

  async function runProcess(element: ModelListElement) {
    const inputFile = getInputFile(element);

    if (!element.model3dEntry) return;
    if (!element.model3dEntry) return;
    if (element.removed) return;
    if (element.status === ModelListElementStatus.CRITICAL_ERROR) return;
    if (element.status === ModelListElementStatus.LAUNCHED) return;
    if (inputFile && !inputFile.isUploaded) return;

    const fileType = element.model3dEntry.inputFileType;
    if (!fileType) return;

    element.status = ModelListElementStatus.LAUNCHING;

    const ext = inputFile?.ext ?? element.model3dEntry.inputFileType;

    let processType = getProcessType(ext);
    if (processType === ProcessType.IFC_XKT && uiStore.localConversionMode)
      processType = ProcessType.CONVERT_METADATA;

    try {
      const { convert } = useHttpClientStore();

      await convert.runProcess({
        model3dEntryId: element.model3dEntry.id,
        type: processType,
      });

      element.status = ModelListElementStatus.LAUNCHED;
    } catch (error) {
      element.status = ModelListElementStatus.RETRIABLE_ERROR;
      element.errorMessage = 'composables.use_model_list.error_convert_element';
      Sentry.captureException(error, {
        fingerprint: ['useProjectStore', 'runProcess', 'model3dEntryId', element.model3dEntry!.id],
      });
    }
  }

  async function runLocalProcess(element: ModelListElement) {
    const inputFile = getInputFile(element);

    if (!inputFile) return;
    if (inputFile.isUploaded) return;
    if (!uiStore.localConversionMode) return;
    if (!project.value?.id || element.model3dEntry) return;
    if (element.removed) return;
    if (element.status === ModelListElementStatus.CRITICAL_ERROR) return;
    if (element.status === ModelListElementStatus.LAUNCHED) return;
    if (element.localConversion.done) return;

    const ext = inputFile.ext;
    const processType = getProcessType(ext);

    return new Promise<void>((resolve) => {
      if (processType === ProcessType.IFC_XKT) {
        element.localConversion.active = true;

        element.localConversion.statusMessage =
          'composables.use_model_list.local_conversion_queue_message';
        element.status = ModelListElementStatus.LOCAL_CONVERTION;

        element.localConversion.worker = new Worker(
          new URL('~/workers/ifc-xkt-worker.ts', import.meta.url),
          { type: 'module' },
        );

        element.localConversion.worker.onmessage = async (event) => {
          const response = event.data as WorkerMessage;
          if (!response) return;

          if (response.type === 'progress') {
            element.localConversion.statusMessage =
              'composables.use_model_list.local_conversion_progress_message';
            element.localConversion.progress = response.data as WorkerMessageData['progress'];
          }

          if (response.type === 'progress-text') {
            element.localConversion.statusMessage =
              'composables.use_model_list.local_conversion_progress_message';
            element.localConversion.progressText =
              response.data as WorkerMessageData['progress-text'];
          }

          if (response.type === 'error') {
            element.status = ModelListElementStatus.LOCAL_CONVERTION_ERROR;
            element.localConversion.statusMessage =
              'composables.use_model_list.local_conversion_error_message';

            element.localConversion.worker!.terminate();
            element.localConversion.worker = null;

            console.error(response.data);
            Sentry.captureException(response.data, {
              fingerprint: ['useProjectStore', 'runLocalProcess', 'fileName', inputFile.name],
            });

            resolve();
          }

          if (response.type === 'done') {
            element.status = ModelListElementStatus.PENDING;
            element.localConversion.done = true;
            element.localConversion.progress = 0;
            element.localConversion.progressText = '';
            element.localConversion.statusMessage = '';

            element.localConversion.worker!.terminate();
            element.localConversion.worker = null;

            const result = response.data as WorkerMessageData['done'];

            if (result.xkt) {
              const xktBlob = new Blob([result.xkt as any], { type: 'application/octet-stream' });
              const xktFile = new File([xktBlob], 'output.xkt', {
                type: 'application/octet-stream',
              });

              element.files.push({
                id: String(Date.now()),
                input: false,
                uploadUrl: null,
                data: xktFile,
                name: xktFile.name,
                mimeType: xktFile.type,
                ext: 'xkt',
                size: xktFile.size,
                progress: 0,
                speed: 0,
                timeLeft: 0,
                abortController: new AbortController(),
                isUploaded: false,
                isReported: false,
              });
            }

            if (result.metadata) {
              const metadataBlob = new Blob([result.metadata], { type: 'application/json' });
              const metadataFile = new File([metadataBlob], 'metadata.json', {
                type: 'application/json',
              });

              element.files.push({
                id: String(Date.now()),
                input: false,
                uploadUrl: null,
                data: metadataFile,
                name: metadataFile.name,
                mimeType: metadataFile.type,
                ext: 'json',
                size: metadataFile.size,
                progress: 0,
                speed: 0,
                timeLeft: 0,
                abortController: new AbortController(),
                isUploaded: false,
                isReported: false,
              });
            }

            resolve();
          }
        };

        element.localConversion.worker.postMessage(inputFile.data);
      }
    });
  }

  async function startModelWorkflow(element: ModelListElement) {
    await runLocalProcess(element);
    await calculateModelHash(element);
    await initializeModel(element);
    await uploadModel(element);
    await runProcess(element);
  }

  async function removeModel(element: ModelListElement) {
    disableRefreshingInterval();
    element.removed = true;

    try {
      if (element.model3dEntry) {
        const { models3d } = useHttpClientStore();
        await models3d.deleteOne(element.model3dEntry.id);
      }

      for (const sceneModelId of element.sceneModelIds) viewerStore.removeModel(sceneModelId);
      modelsList.value = modelsList.value.filter((item) => item !== element);
    } catch (error) {
      Sentry.captureException(error, {
        fingerprint: ['useProjectStore', 'removeModel', element.model3dEntry?.id ?? element.name],
      });
      console.log({ removeModelError: error });
    }

    enableRefreshingInterval();
  }

  function cancelModel(element: ModelListElement) {
    for (const file of element.files) file.abortController.abort();
  }

  function findModelBySceneModel(sceneModelId: string) {
    return modelsList.value.find((element) => element.sceneModelIds.includes(sceneModelId));
  }

  function updateStats() {
    modelsStats.value.inProgress = 0;
    modelsStats.value.uploaded = 0;
    modelsStats.value.uploading = 0;
    modelsStats.value.converting = 0;
    modelsStats.value.converted = 0;

    const maxSteps = 7;
    let conversionProgress = 0;
    let uploadProgress = 0;
    let conversionModelsCount = 0;
    let uploadingModelsCount = 0;

    for (const model of modelsList.value) {
      const file = getInputFile(model);

      if (file && model.status === ModelListElementStatus.UPLOADING) {
        uploadingModelsCount++;
        uploadProgress += file.progress / 100;
        modelsStats.value.uploading++;
        modelsStats.value.inProgress++;
      }

      switch (model.status) {
        case ModelListElementStatus.LAUNCHING:
        case ModelListElementStatus.LAUNCHED:
        case ModelListElementStatus.UPLOADED:
          modelsStats.value.uploaded++;
          break;
      }

      let conversionStep = 0;

      if (model.model3dEntry && model.status === ModelListElementStatus.LAUNCHED) {
        switch (model.model3dEntry.status) {
          case Model3dEntryStatus.PROCESS_PREPARING:
            modelsStats.value.converting++;
            modelsStats.value.inProgress++;
            conversionStep = 1;
            break;

          case Model3dEntryStatus.PROCESS_CONVERTING:
            modelsStats.value.converting++;
            modelsStats.value.inProgress++;
            conversionStep = 2;
            break;

          case Model3dEntryStatus.PROCESS_FINALIZING:
            modelsStats.value.converting++;
            modelsStats.value.inProgress++;
            conversionStep = 3;
            break;

          case Model3dEntryStatus.PROCESS_COMPLETED:
            modelsStats.value.converted++;
            conversionStep = 4;
            break;

          case Model3dEntryStatus.PROCESS_ERROR:
            conversionStep = -1;
            break;
        }
      }

      if (conversionStep >= 0) {
        conversionProgress += conversionStep / maxSteps;
        conversionModelsCount++;
      }
    }

    if (conversionModelsCount > 0)
      modelsStats.value.conversionProgress = conversionProgress / conversionModelsCount;
    else modelsStats.value.conversionProgress = 1;

    if (uploadingModelsCount > 0)
      modelsStats.value.uploadProgress = uploadProgress / uploadingModelsCount;
    else modelsStats.value.uploadProgress = 1;
  }

  function loadToViewer(element: ModelListElement) {
    queueStore.clearCompletedJobs();

    if (!element.model3dEntry?.model3dOutputs) return;
    if (element.model3dEntry.status !== Model3dEntryStatus.PROCESS_COMPLETED) return;

    const id = element.model3dEntry.id;

    const xktSources = element.model3dEntry.model3dOutputs.reduce((result, output) => {
      if (output.fileType === ProcessFileType.XKT) result.push(output.url);
      return result;
    }, [] as string[]);

    // GLB is produced as a downloadable artifact alongside the XKT for IFC/RVT/STEP/NWD
    // conversions. When an XKT output exists we view the XKT, not the GLB.
    const gltfSource =
      xktSources.length > 0
        ? undefined
        : element.model3dEntry.model3dOutputs.find(
            (output) => output.fileType === ProcessFileType.GLB,
          );

    const lasSource = element.model3dEntry.model3dOutputs.find(
      (output) => output.fileType === ProcessFileType.LAS,
    );

    const bimSource = element.model3dEntry.model3dOutputs.find(
      (output) => output.fileType === ProcessFileType.BIM,
    );

    const objSource = element.model3dEntry.model3dOutputs.find(
      (output) => output.fileType === ProcessFileType.OBJ,
    );

    const stlSource = element.model3dEntry.model3dOutputs.find(
      (output) => output.fileType === ProcessFileType.STL,
    );

    const metadataSource =
      element.model3dEntry.inputFileType === ProcessFileType.IFC ||
      element.model3dEntry.inputFileType === ProcessFileType.RVT ||
      element.model3dEntry.inputFileType === ProcessFileType.RFA ||
      element.model3dEntry.inputFileType === ProcessFileType.STEP ||
      element.model3dEntry.inputFileType === ProcessFileType.NWD ||
      element.model3dEntry.inputFileType === ProcessFileType.NWC
        ? element.model3dEntry.model3dOutputs.find(
            (output) => output.fileType === ProcessFileType.JSON,
          )
        : undefined;

    element.viewer.status = ModelListElementViewerStatus.LOADING;

    element.model3dEntry.model3dOutputs?.forEach((output) => {
      if (
        output.fileType !== ProcessFileType.MTL &&
        output.fileType !== ProcessFileType.DB &&
        output.fileType !== ProcessFileType.JSON &&
        output.fileType !== ProcessFileType.LOG &&
        !(output.fileType === ProcessFileType.GLB && !gltfSource)
      ) {
        projectModels.value.push({
          id: output.id,
          url: output.url,
          type: '',
          stage: 1,
          percent: 0,
          loaded: 0,
          total: 0,
          current: false,
        });
      }
    });

    queueStore.addJob({
      id,
      handler: async () => {
        try {
          let sceneModelId: string | undefined = undefined;

          if (xktSources.length > 0)
            sceneModelId = await loadModelXKT(`${id}-xkt`, xktSources, {
              edges: project.value?.showEdges,
              pbr: project.value?.enablePbr,
              metadataURL: metadataSource?.url,
            });

          if (gltfSource)
            sceneModelId = await loadModelGLTF(`${id}-gltf`, gltfSource.url, gltfSource.fileName, {
              edges: project.value?.showEdges,
              pbr: project.value?.enablePbr,
            });

          if (lasSource) {
            sceneModelId = await loadModelLAS(
              `${id}-las`,
              lasSource.url,
              lasSource.fileName,
              lasSource.fileSize,
              {
                fp64: project.value?.enableFp64,
              },
            );

            hasPointCloudModel.value = true;
          }

          if (bimSource) {
            element.viewer.rotationOffsetX = -90;

            sceneModelId = await loadModelBIM(`${id}-bim`, bimSource.url, bimSource.fileName, {
              edges: project.value?.showEdges,
              pbr: project.value?.enablePbr,
            });
          }

          if (objSource)
            sceneModelId = await loadModelOBJ(`${id}-obj`, objSource.url, objSource.fileName, {
              edges: project.value?.showEdges,
              pbr: project.value?.enablePbr,
            });

          if (stlSource)
            sceneModelId = await loadModelSTL(`${id}-stl`, stlSource.url, stlSource.fileName, {
              edges: project.value?.showEdges,
              pbr: project.value?.enablePbr,
            });

          if (sceneModelId) {
            element.sceneModelIds.push(sceneModelId);
            const model = viewerStore.getHelpers().viewer?.scene.models[sceneModelId];

            if (model) {
              // @ts-ignore xeokit
              if (model.numEntities === 0 && model.numGeometries === 0) {
                toast.error(t('notifications.corrupted_model'), {
                  description: t('notifications.corrupted_model.description', {
                    name: element.name,
                  }),
                  duration: 5000,
                });
              } else {
                const modelCenterPosition = getAABBCenter(model.aabb);
                element.viewer.positionOffsetX = modelCenterPosition[0];
                element.viewer.positionOffsetY = modelCenterPosition[1];
                element.viewer.positionOffsetZ = modelCenterPosition[2];

                updateModel(model, {
                  position: [
                    element.model3dEntry?.userPositionX,
                    element.model3dEntry?.userPositionY,
                    element.model3dEntry?.userPositionZ,
                  ],
                  rotation: [
                    (element.model3dEntry?.userRotationX ?? 0) + element.viewer.rotationOffsetX,
                    (element.model3dEntry?.userRotationY ?? 0) + element.viewer.rotationOffsetY,
                    (element.model3dEntry?.userRotationZ ?? 0) + element.viewer.rotationOffsetZ,
                  ],
                  scale: [
                    element.model3dEntry?.userScaleX ?? 1,
                    element.model3dEntry?.userScaleY ?? 1,
                    element.model3dEntry?.userScaleZ ?? 1,
                  ],
                });

                if (project.value?.showBoundingBox) {
                  viewerStore.drawModelBoundingBox(sceneModelId, model.aabb);
                }
              }
            }
          }

          element.viewer.status = ModelListElementViewerStatus.LOADED;
          viewerStore.updateDollySpeed();
          updateIfcTypesList();
        } catch (error) {
          console.log({ loadModelError: error });
          element.viewer.status = ModelListElementViewerStatus.ERROR;
          if (error instanceof Error) element.viewer.errorMessage = error.message;
          else element.viewer.errorMessage = String(error);

          Sentry.captureException(error, {
            fingerprint: ['useProjectStore', 'loadToViewer', 'model3dEntryId', id],
          });
        }
      },
    });

    queueStore.addJob({
      id: `${id}-scene-meta-models`,
      handler: async () => {
        const viewer = viewerStore.getHelpers().viewer;
        metaObjectsArray = Object.entries(viewer!.metaScene.metaObjects);
      },
    });

    queueStore.addJob({
      id: `${id}-check-far-away-objects`,
      handler: async () => {
        viewerStore.checkFarAwayObjects();
      },
    });

    queueStore.addJob({
      id: `${id}-view-fit`,
      handler: async () => {
        viewerStore.viewFit();
      },
    });
  }

  function unloadFromViewer(element: ModelListElement) {
    for (const sceneModelId of element.sceneModelIds) viewerStore.removeModel(sceneModelId);
    element.sceneModelIds = [];
  }

  function disableRefreshingInterval() {
    refreshIntervalEnabled.value = false;
    if (refreshAbortController) refreshAbortController.abort();
  }

  function enableRefreshingInterval() {
    refreshIntervalEnabled.value = true;
  }

  function getInputFile(element: ModelListElement) {
    return element.files.find((file) => file.input);
  }

  function getProjectInputsSize(): number {
    let projectInputsSize = 0;
    project.value?.model3dEntries?.forEach((model3dEntry) => {
      if (!model3dEntry.inputFileSize) return;
      projectInputsSize += model3dEntry.inputFileSize;
    });
    return projectInputsSize;
  }

  function getProjectInputsCount(): number {
    return project.value?.model3dEntries?.length ?? 0;
  }

  function updateIfcTypesList() {
    const ifcTypes = viewerStore.getCurrentIfcTypes();
    if (ifcTypes.length === 0) return;

    ifcTypes.forEach((type) => {
      if (!uiStore.ifcTypesList.includes(type)) {
        uiStore.ifcTypesList.push(type);
      }
    });

    uiStore.ifcTypesList.sort((a, b) => a.localeCompare(b));
  }

  return {
    project,
    modelsList,
    modelsStats,
    metaObjectsArrayReady,
    refreshIntervalEnabled,
    validExtensions,
    hasPointCloudModel,
    createProject,
    cloneProject,
    refreshProject,
    createModel,
    initializeModel,
    uploadModel,
    runProcess,
    startModelWorkflow,
    removeModel,
    cancelModel,
    findModelBySceneModel,
    getMetaObjectsArray,
    loadToViewer,
    unloadFromViewer,
    disableRefreshingInterval,
    enableRefreshingInterval,
    getInputFile,
    getProjectInputsSize,
    getProjectInputsCount,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProjectStore, import.meta.hot));
}
